import { useLazyQuery } from '@apollo/client/react'
import { useCallback, useMemo, useState, useRef } from 'react'
import {
  useDebounceCallback,
  toast,
  Heading,
  Text,
  Link,
} from '@buffer-mono/popcorn'

import { graphql } from '../../../gql'
import type { ObPlanId, BillingInterval } from '../../../gql/graphql'
import { OBPlanIdValues } from '../types'

const GetPreviewInvoice = graphql(/* GraphQL */ `
  query GetPreviewInvoice(
    $organizationId: OrganizationId!
    $plan: OBPlanId!
    $interval: BillingInterval!
    $quantity: Int!
  ) {
    getPreviewInvoice(
      input: {
        organizationId: $organizationId
        plan: $plan
        interval: $interval
        quantity: $quantity
      }
    ) {
      ... on GetPreviewInvoiceResponse {
        chargeAmount
        creditAmount
        isScheduledChange
        nextBillingDate
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

interface GetCustomInvoiceOptions {
  organizationId: string
  planId: ObPlanId
  interval: BillingInterval
  quantity: number
}

interface UseGetCustomInvoiceOptions {
  /**
   * Whether to show a toast notification when the invoice preview fails to load.
   * Set to false if you're handling errors with your own UI (e.g., empty state).
   * @default true
   */
  showErrorToast?: boolean
}

interface UseGetCustomInvoiceReturn {
  getCustomInvoice: {
    (options: GetCustomInvoiceOptions): Promise<void> | undefined
    isPending(): boolean
    flush(): void
    cancel(): void
  }
  loading: boolean
  error: Error | undefined
  data: CustomInvoiceData | undefined
  clearData: () => void
}

interface CustomInvoiceData {
  nextBillingDate: string
  isScheduledChange: boolean
  dueNow: number
}

const formatDateString = (dateString: string | null | undefined): string => {
  if (!dateString) return ''

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(String(dateString)))
}

/**
 * Hook to get the custom invoice data for displaying payment previews to customers.
 *
 * This hook fetches invoice preview data when the user changes their channel quantity,
 * showing them how much they'll be charged (prorated for mid-cycle changes).
 *
 * @param options - Configuration options
 * @param options.showErrorToast - Whether to show a toast notification on error.
 *   Set to `false` if you're displaying errors in your own UI (e.g., with an empty state).
 *   Defaults to `true` to show a toast with a "Contact Support" link.
 *
 * @example
 * ```tsx
 * // With error toast (default behavior)
 * const { getCustomInvoice, data, loading, error } = useGetCustomInvoice()
 *
 * // Without error toast (handling errors yourself)
 * const { getCustomInvoice, data, loading, error } = useGetCustomInvoice({
 *   showErrorToast: false
 * })
 *
 * // Then handle the error in your UI
 * if (error) {
 *   return <EmptyState>Unable to load pricing</EmptyState>
 * }
 * ```
 *
 * @returns Hook interface with invoice data and loading/error states
 */
export function useGetCustomInvoice(
  options: UseGetCustomInvoiceOptions = {},
): UseGetCustomInvoiceReturn {
  const { showErrorToast = true } = options
  const [fetchPreviewInvoice, { loading, error }] =
    useLazyQuery(GetPreviewInvoice)

  // Internal state to hold the calculated invoice data
  const [invoiceData, setInvoiceData] = useState<CustomInvoiceData | undefined>(
    undefined,
  )

  // Track request ID to prevent race conditions
  const requestIdRef = useRef(0)

  const getCustomInvoiceImmediate = useCallback(
    async (options: GetCustomInvoiceOptions): Promise<void> => {
      const { organizationId, planId, interval, quantity } = options

      // Increment and capture the request ID to track this specific request.
      // This prevents race conditions where older requests complete after newer ones,
      // or when clearData() is called while requests are in-flight.
      const currentId = ++requestIdRef.current

      // Skip for free plans - they never have a charge
      if (planId === OBPlanIdValues.free) {
        if (currentId !== requestIdRef.current) return
        setInvoiceData({
          dueNow: 0,
          nextBillingDate: '',
          isScheduledChange: false,
        })
        return
      }

      try {
        const result = await fetchPreviewInvoice({
          variables: {
            organizationId,
            plan: planId,
            interval,
            quantity,
          },
        })

        if (result.error) {
          throw result.error
        }

        const response = result.data?.getPreviewInvoice
        if (currentId !== requestIdRef.current) return

        if (response?.__typename === 'VoidMutationError') {
          // Handle error response
          setInvoiceData(undefined)
          return
        }

        if (response?.__typename === 'GetPreviewInvoiceResponse') {
          const chargeAmount = response.chargeAmount ?? 0
          const isScheduledChange = response.isScheduledChange ?? false

          if (currentId !== requestIdRef.current) return
          setInvoiceData({
            dueNow: chargeAmount,
            nextBillingDate: formatDateString(response.nextBillingDate),
            isScheduledChange,
          })
        } else {
          setInvoiceData(undefined)
        }
      } catch {
        if (currentId === requestIdRef.current) {
          if (showErrorToast) {
            toast.error(
              <>
                <Heading size="xsmall" style={{ marginBottom: '0.5rem' }}>
                  Unable to load payment amount
                </Heading>
                <Text>
                  We&apos;ve logged the issue and we&apos;re working on a fix.
                  For help in the meantime,{' '}
                  <Link
                    as="button"
                    onClick={(): void => {
                      window.Brainfish?.Widgets?.open()
                    }}
                  >
                    contact support
                  </Link>
                  .
                </Text>
              </>,
            )
          }
          setInvoiceData(undefined)
        }
      }
    },
    [fetchPreviewInvoice, showErrorToast],
  )

  // Debounced version with 500ms delay - this is the main export
  const getCustomInvoice = useDebounceCallback(getCustomInvoiceImmediate, 500)

  // Function to clear the invoice data
  const clearData = useCallback(() => {
    // Drop any queued calls and invalidate in-flight responses
    getCustomInvoice.cancel()
    requestIdRef.current++
    setInvoiceData(undefined)
  }, [getCustomInvoice])

  return useMemo(
    () => ({
      getCustomInvoice,
      loading,
      error: error || undefined,
      data: invoiceData,
      clearData,
    }),
    [getCustomInvoice, loading, error, invoiceData, clearData],
  )
}
